import { supabase } from './supabase';
import { isEmailValid } from './signup';

/**
 * Authentification (brique H) — connexion / déconnexion / session courante.
 * Le SuperAdmin et les RPC sécurisées dépendent de la session Supabase du navigateur :
 * il faut être connecté avec un compte super_admin pour que `auth.uid()` corresponde.
 */

export interface SignInResult {
  ok: boolean;
  error?: string;
}

/** Valide le formulaire de connexion (email + mot de passe non vide). */
export function validateLogin(email: string, password: string): string | null {
  if (!isEmailValid(email)) return 'Email invalide.';
  if (password.trim().length < 1) return 'Mot de passe requis.';
  return null;
}

/** Connecte un utilisateur par email + mot de passe. */
export async function signIn(email: string, password: string): Promise<SignInResult> {
  const invalid = validateLogin(email, password);
  if (invalid) return { ok: false, error: invalid };

  const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
  if (error) {
    return { ok: false, error: 'Connexion impossible : ' + error.message };
  }
  return { ok: true };
}

/**
 * Déconnecte l'utilisateur courant — scope LOCAL uniquement.
 * On n'efface que la session de CE navigateur/app, on NE révoque PAS les autres sessions
 * (le scope 'global' par défaut déconnecterait aussi l'admin legacy qui partage le même
 * projet Supabase). Incident 12/06.
 */
export async function signOut(): Promise<void> {
  await supabase.auth.signOut({ scope: 'local' });
}

/** Email de l'utilisateur connecté, ou null si aucune session. */
export async function currentEmail(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data.user?.email ?? null;
}

/* ============ Réinitialisation du mot de passe (self-service gérant) ============ */

/** Valide le nouveau mot de passe (longueur min + confirmation). Pur, testable. */
export function validateNewPassword(password: string, confirm: string): string | null {
  if (password.length < 8) return 'Le mot de passe doit faire au moins 8 caractères.';
  if (password !== confirm) return 'Les deux mots de passe ne correspondent pas.';
  return null;
}

/**
 * Envoie un email de réinitialisation. Le lien ramène le gérant sur `redirectTo`
 * (typiquement l'URL admin de SON resto) en mode « recovery » → il choisit un
 * nouveau mot de passe. ⚠️ `redirectTo` doit être autorisé dans Supabase
 * (Authentication → URL Configuration → Redirect URLs).
 */
export async function requestPasswordReset(
  email: string,
  redirectTo?: string,
): Promise<{ ok: boolean; error?: string }> {
  if (!isEmailValid(email)) return { ok: false, error: 'Email invalide.' };
  const { error } = await supabase.auth.resetPasswordForEmail(
    email.trim(),
    redirectTo ? { redirectTo } : undefined,
  );
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

/** Définit le nouveau mot de passe (en session « recovery » après clic sur l'email). */
export async function updatePassword(newPassword: string): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

/**
 * S'abonne à l'événement « PASSWORD_RECOVERY » (déclenché quand le gérant arrive
 * via le lien de l'email). Renvoie une fonction de désabonnement.
 */
export function onPasswordRecovery(callback: () => void): () => void {
  const { data } = supabase.auth.onAuthStateChange((event) => {
    if (event === 'PASSWORD_RECOVERY') callback();
  });
  return () => data.subscription.unsubscribe();
}
